import { useTranslation } from "react-i18next";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import { useNavigate, useSearchParams } from "react-router-dom";
import IconButton from "../../../../components/Buttons/IconButton";
import { IoCreateOutline } from "react-icons/io5";
import { BsTrash3 } from "react-icons/bs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { del, get } from "../../../../utils/helpers/api";
import { AgGridReact } from "ag-grid-react";
import DefaultPagination from "../../../../components/Pagination";
import FiltersDrawer from "./Drawers/Filters";
import { Badge } from "reactstrap";
import { TfiFilter } from "react-icons/tfi";
import { printSlug } from "../../../../utils/helpers/text";
import ConfirmationModal from "../../../../components/Modals/Confirmation";
import { toast } from "react-toastify";
import AgGridTable from "../../../../components/Tables/AgGrid";

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.navigate(`/accounting/expenses/edit/${data.id}`, { state: { expenseData: data } });
        }}
        size="sm" outline color="secondary"
      >
        <IoCreateOutline />
      </IconButton>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.setExpenseToDelete(data.id);
        }}
        size="sm" outline color="danger"
      >
        <BsTrash3 />
      </IconButton>
    </div>
  );
}

function ExpensesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const FILTER_KEYS = ['search', 'added_after', 'added_before'];

  const [filters, setFilters] = useState(() => {
    const initial = {};
    FILTER_KEYS.forEach(key => {
      const val = searchParams.get(key);
      if (val) initial[key] = val;
    });
    return initial;
  });

  const filtersCount = Object.values(filters).filter(v => v).length;

  const getExpenses = useCallback(async () => {
    setLoading(true);
    let query = `/accounting/expenses?page=${currentPage}&page_size=${pageSize}`;
    const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    Object.entries(activeFilters).forEach(([key, value]) => {
      query += `&${key}=${encodeURIComponent(value)}`;
    });
    const data = await get(query);
    if (data?.expenses?.data) {
      setRowData(data.expenses.data);
      setTotalPages(data.expenses.last_page);
      setTotalRecords(data.expenses.total);
    }
    setLoading(false);
  }, [currentPage, pageSize, filters]);

  useEffect(() => {
    getExpenses();
  }, [getExpenses]);

  const deleteExpense = async () => {
    const data = await del('/accounting/expenses/delete/' + expenseToDelete);

    if (data?.success) {
      toast.success(t('accounting.expenseDeleted'));
      getExpenses();
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setExpenseToDelete(null);
  }

  const handleApplyFilters = (filtersData) => {
    const activeFilters = Object.fromEntries(Object.entries(filtersData).filter(([, v]) => v));
    setFilters(activeFilters);
    setSearchParams(activeFilters);
    setCurrentPage(1);
    setFiltersOpen(false);
  }

  const columnDefs = useMemo(() => [
    { field: 'title', headerName: t('common.title'), minWidth: 200, flex: 1, sortable: true },
    { field: 'date', headerName: t('common.date'), minWidth: 200, flex: 1, sortable: true },
    { field: 'category', headerName: t('accounting.category'), minWidth: 120, flex: 1, sortable: true },
    {
      field: 'amount',
      headerName: t('common.amount'),
      minWidth: 160,
      flex: 1,
      sortable: true,
      valueFormatter: (params) => {
        return params?.data?.amount + ' ' + params?.data?.currency;
      }
    },
    {
      field: 'payment_method',
      headerName: t('accounting.paymentMethod'),
      minWidth: 120,
      flex: 1, 
      sortable: true,
      valueFormatter: (params) => {
        return printSlug(params.value);
      }
    },
    { headerName: t('common.actions'), minWidth: 100, flex: 1, sortable: false, resizable: false, cellRenderer: ActionsCellRenderer },
  ], [navigate, t]);

  const defaultColDef = useMemo(() => ({ resizable: true }), []);
  const gridContext = useMemo(() => ({ navigate, setExpenseToDelete }), [navigate]);

  return(
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('accounting.expenses')} onReload={getExpenses} dataTotal={totalRecords}>
          <button className="subheader-btn" type="button" onClick={() => setFiltersOpen(true)}>
            <TfiFilter size={13} />
            {t('common.filter')}
            {filtersCount > 0 && (
              <Badge color="primary" className="filter-badge">{filtersCount}</Badge>
            )}
          </button>
          <div className="subheader-divider" />
          <DefaultButton color="primary" size="sm" onClick={() => navigate('/accounting/expenses/create')}>
            + {t('accounting.newExpense')}
          </DefaultButton>
        </PageSubHeader>

        <div className="subheader-page-content">
          <AgGridTable 
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            context={gridContext}
          />

          <DefaultPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      <ConfirmationModal
        isOpen={expenseToDelete !== null}
        title={t('common.deleteConfirmTitle')}
        message={t('accounting.deleteExpenseConfirm')}
        onConfirm={deleteExpense}
        toggle={() => setExpenseToDelete(null)}
      />

      <FiltersDrawer
        isOpen={filtersOpen}
        toggle={() => setFiltersOpen(false)}
        filters={filters}
        applyFilters={handleApplyFilters}
      />
    </SidebarLayout>
  );
}

export default ExpensesPage;
